import { AuthenticationResult } from '@azure/msal-browser';
import axios, { AxiosInstance } from 'axios';
import AuthService from './AuthService';

import { tramesesFormType } from '../interface/trameses';
import { tramesesGroupedType } from '../interface/trameses';

class ApiService {
    // constructor that requires a base URI.
    constructor(baseUri: string, authService: AuthService) {
        if (!baseUri) {
            throw new Error('the base uri was not provided');
        }
        if (!authService) {
            throw new Error('the auth service was not provided');
        }

        this.AuthorizationService = authService;

        this.AuthenticatedApi = axios.create({
            baseURL: baseUri,
            // will inject auth header on-demand later as needed.
            headers: {
                'Content-Type': 'application/json',
            }
        });
    }

    // the authenticated api
    AuthenticatedApi: AxiosInstance;

    // the authorization service
    // wrapper around MSAL
    AuthorizationService: AuthService;

    // an api operation that calls one of the anonymous / no-auth endpoints.
    InvokeNoAuthApiCall() {
        return this.AuthenticatedApi.get('/test/noauth')
            .then((response: any) => {
                return response.data;
            })
            .catch((error: any) => {
                throw Error('An error has occurred calling the api: ' + error);
            });
    }

    InvokeUserApiCall() {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get('/test/UserAccess', {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    // an api operation that calls one of the admin user authorized endpoints.
    InvokeAdminApiCall() {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get('/test/admin', {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetUser() {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetUser`,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }


    GetRelevantInformation(type: string) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetRelevantInformation/${type}`,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetTramesesTypes(param: string, value: string) {

        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetTramesesTypes?${encodeURIComponent(param)}=${encodeURIComponent(value)}`,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {

                        const trameses = (response.data.trameses || []).map((t: any) => ({
                            value: t.id.toString(),
                            label: t.titulo,
                            periodicity: t.fields?.Periodicity?.value || null,
                            startDate: t.fields?.StartDate || null,
                            initialMonth: t.fields?.InitialMonth || null,
                        }));

                        return trameses;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetTramesesByTypes(id: string) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetFormByTramesa/${encodeURIComponent(id)}`,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        const fields: tramesesFormType[] = response.data.fields || [];

                        const grouped = fields.reduce<Record<string, tramesesFormType[]>>((acc, field) => {
                            const group = field.groupName ?? "";
                            if (!acc[group]) {
                                acc[group] = [];
                            }
                            acc[group]!.push(field);
                            return acc;
                        }, {});

                        const result: tramesesGroupedType[] = Object.entries(grouped).map(
                            ([groupName, groupFields]) => ({
                                groupName,
                                fields: groupFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
                            })
                        );

                        return result;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }


    GetTramesesFilial(entity: string) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetFilialsFromEntity/${encodeURIComponent(entity)}`,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        const filial = (response.data.user.filials || []).map((t: any) => ({
                            value: t.lookupId.toString(),
                            label: t.lookupValue
                        }));

                        return filial;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }


    CreateTramesa(post: any) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.post(`/api/CreateTramesa`,
                    post,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response;
                    })
                    .catch((error: any) => {
                        const jsonString = JSON.stringify(post);
                        const bodySizeInBytes = new Blob([jsonString]).size;
                        if (bodySizeInBytes > 104857600) {
                            throw Error("La mida dels fitxer no pot ser superior a 100 MB.")
                        }
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                const jsonString = JSON.stringify(post);
                const bodySizeInBytes = new Blob([jsonString]).size;
                if (bodySizeInBytes > 104857600) {
                    throw Error("La mida dels fitxer no pot ser superior a 100 MB.")
                }
                throw Error('An error has occurred: ' + error);
            });
    }

    GetComunicaciones() {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetCommunications`,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetTramesesDashboard(entityId: any) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetTramesesDashboard?entityId=` + entityId,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetTramitsDashboard(entityId: any) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetTramitsDashboard?entityId=` + entityId,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetTrameses(entityId: any, year?: any) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetTrameses?entityId=` + entityId + (year != null ? "&year=" + year : ""),
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetTramits(entityId: any, year?: any) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetTramits?entityId=` + entityId + (year != null ? "&year=" + year : ""),
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetTramesaById(tramesaId: any, entityId: any) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetTramesaById/` + tramesaId + "?entityId=" + entityId,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data.tramesa;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetTramesesControl(entity: string, year?: string) {
        var data = `/api/GetTramesesControlWithHolidays?${entity}` + (year ? `&year=${year}` : "");

        console.log('Quadre Tramesas: ' + data)
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetTramesesControlWithHolidays?${entity}` + (year ? `&year=${year}` : ""),
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    UpdateTramesa(id: string, post: any) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.put(`/api/UpdateTramesa/${id}`,
                    post,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response;
                    })
                    .catch((error: any) => {
                        const jsonString = JSON.stringify(post);
                        const bodySizeInBytes = new Blob([jsonString]).size;
                        if (bodySizeInBytes > 104857600) {
                            throw Error("La mida dels fitxer no pot ser superior a 100 MB.")
                        }
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                const jsonString = JSON.stringify(post);
                const bodySizeInBytes = new Blob([jsonString]).size;
                if (bodySizeInBytes > 104857600) {
                    throw Error("La mida dels fitxer no pot ser superior a 100 MB.")
                }
                throw Error('An error has occurred: ' + error);
            });
    }

    UpdateTramit(id: string, post: any) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.put(`/api/UpdateTramit/${id}`,
                    post,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response;
                    })
                    .catch((error: any) => {
                        const jsonString = JSON.stringify(post);
                        const bodySizeInBytes = new Blob([jsonString]).size;
                        if (bodySizeInBytes > 104857600) {
                            throw Error("La mida dels fitxer no pot ser superior a 100 MB.")
                        }
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                const jsonString = JSON.stringify(post);
                const bodySizeInBytes = new Blob([jsonString]).size;
                if (bodySizeInBytes > 104857600) {
                    throw Error("La mida dels fitxer no pot ser superior a 100 MB.")
                }
                throw Error('An error has occurred: ' + error);
            });
    }

    DeleteTramesa(id: string) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.delete(`/api/DeleteTramesa/${id}`,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response;
                    })
                    .catch((error: any) => {
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    DeleteTramit(id: string) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.delete(`/api/DeleteTramit/${id}`,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response;
                    })
                    .catch((error: any) => {
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetTramitsTypes(param: string, value: string) {

        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetTramitsTypes?${encodeURIComponent(param)}=${encodeURIComponent(value)}`,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        const tramitsTipus = [...new Map((response.data.tramits || []).map((t: any) => [
                            t.id.toString(),
                            {
                                label: t?.fields?.ProcessType,
                                value: t?.fields?.ProcessType
                            }
                        ])
                        ).values()
                        ];

                        const tramitsProces = (response.data.tramits || []).map((t: any) => ({
                            value: t.id.toString(),
                            label: t.titulo,
                            processType: t?.fields?.ProcessType,
                            previousProcess: t?.fields?.PreviousProcess?.id,
                        }));
                        const distinctOptions = Array.from(
                            new Map(tramitsTipus.map((item: any) => [item.label + "|" + item.value, item])).values()
                        );

                        return { tipus: distinctOptions, proces: tramitsProces };
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }


    CreateTramit(post: any) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.post(`/api/CreateTramit`,
                    post,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response;
                    })
                    .catch((error: any) => {
                        const jsonString = JSON.stringify(post);
                        const bodySizeInBytes = new Blob([jsonString]).size;
                        if (bodySizeInBytes > 104857600) {
                            throw Error("La mida dels fitxer no pot ser superior a 100 MB.")
                        }
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                const jsonString = JSON.stringify(post);
                const bodySizeInBytes = new Blob([jsonString]).size;
                if (bodySizeInBytes > 104857600) {
                    throw Error("La mida dels fitxer no pot ser superior a 100 MB.")
                }
                throw Error('An error has occurred: ' + error);
            });
    }

    GetTramitById(tramitId: any, entityId: any) {

        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetTramitById/` + tramitId + "?entityId=" + entityId,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data.tramit;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }


    GetTramitByTypeId(proces: any, entityId: any) {

        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetTramitByTypeId/` + proces + '?entityId=' + entityId,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }


    GetTramitsById(tramitId: any, entityId: any) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetTramitById/` + tramitId + "?entityId=" + entityId,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data.tramit;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetPendingItems(param: string, value: string) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetPendingItems?${encodeURIComponent(param)}=${encodeURIComponent(value)}`,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }


     GetDocumentation(entityId: any) {
        return this.AuthorizationService.GetToken()
            .then((response: AuthenticationResult) => {
                return this.AuthenticatedApi.get(`/api/GetDocumentation` + "?entityId=" + entityId,
                    {
                        headers: {
                            Authorization: 'Bearer ' + response.accessToken
                        }
                    })
                    .then((response: any) => {
                        return response.data;
                    })
                    .catch((error: any) => {
                        console.log(error)
                        throw Error('An error has occurred calling the web api: ' + error);
                    });
            })
            .catch((error: any) => {
                throw Error('An error has occurred: ' + error);
            });
    }

    GetHoliDays() {
    return this.AuthorizationService.GetToken()
        .then((response: AuthenticationResult) => {
            return this.AuthenticatedApi.get(`/api/GetHoliDays`,
                {
                    headers: {
                        Authorization: 'Bearer ' + response.accessToken
                    }
                })
                .then((response: any) => {
                    return response.data;
                })
                .catch((error: any) => {
                    console.log(error)
                    throw Error('An error has occurred calling the web api: ' + error);
                });
        })
        .catch((error: any) => {
            throw Error('An error has occurred: ' + error);
        });
    }
}

export default ApiService;